import React, { useEffect, useState, useContext, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import styles from './orders-details.module.scss'
import { IOrder, IState } from 'src/Interface'
import Button from 'src/components//button/Button'
import { StateContext, DispatchContext } from '../../AppContext'
import axios from 'axios'
import Map from 'src/components/map/Map'
import useSocket from 'src/hooks/useSocket'
import { baseApiURL, baseApiSocketURL } from 'src/utils/utils'
import { differenceInMinutes, format } from 'date-fns'
import ReactToPrint from 'react-to-print'
import { toast } from 'react-toastify'
import Spinner from 'src/components/spinners/Spinner'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaCancelledTripMapper, gaUserMapper } from 'src/utils/ga/gaMapper'

interface IProps {
  close?: any
  referenceLabel: string
  orderId
}


const OrdersDetails: React.FunctionComponent<IProps> = ({ close, referenceLabel, orderId }: IProps) => {
  const orderDetailsRef = useRef(null)
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const [currentOrderId, setCurrentOrderId] = useState(orderId)
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const [pickupLocation, setPickupLocation] = useState(null)
  const [destination, setDestination] = useState(null)
  const [taxiLocation, setTaxiLocation] = useState(null)
  const [geoJson, setGeoJson] = useState(null)
  const state: IState = useContext(StateContext)
  const order: IOrder = state.userOrders.find((o: any) => o.id === Number(currentOrderId))
  const currentIndex: number = state.userOrders.findIndex((o: any) => o.id === Number(currentOrderId))
  const { cancellablePromise } = useCancellablePromise()
  const stateOrder = [
    'created', 
    'booked', 
    'dispatching', 
    'blocked', 
    'arriving', 
    'customer',
    'taximeter_started', 
    'noshow', 
    'trip_started', 
    'completed', 
    'concluded', 
    'cancelled', 
    'deleted'
  ];
  const canCancel = stateOrder.indexOf(order.state) <= stateOrder.indexOf('trip_started');
  const socketData = useSocket(state.user.accessToken && (order.state !== 'concluded' && order.state !== 'completed') ? `${baseApiSocketURL()}/orders/${currentOrderId}/location?access_token=${state.user.accessToken}` : null)

  useEffect(() => {
    if (state.user.accessToken && order && (order.state === 'concluded' || order.state === 'completed')) {
      cancellablePromise(axios(`${baseApiURL()}/orders/${currentOrderId}/coordinates`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        setGeoJson(result.data);
        setPickupLocation(null)
        setDestination(false)
        setTaxiLocation(null)
      }))
    }
  }, [currentOrderId, state.userOrders, state.user.accessToken, order, cancellablePromise])



  useEffect(() => {
    if (socketData) {
      const { taxi, pickUp, destination } = socketData;
      setPickupLocation(pickUp)
      setTaxiLocation(taxi)
      setDestination(destination)
      setGeoJson(null)
    }
  }, [socketData]);


  if (!order) {
    history.push('/order')
    return null
  }

  const cancelOrder = () => {
    setLoading(true)
    axios.delete(`${baseApiURL()}/orders/${currentOrderId}`,
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {


        setLoading(false)
        const ordersCopy = [...state.userOrders]
        const orderIndex = ordersCopy.findIndex(o => Number(o.id) === Number(currentOrderId))
        const order = state.userOrders[orderIndex]
        order.state = 'cancelled'
        ordersCopy.splice(orderIndex, 1, order);
        dispatch({ type: 'SET_USER_ORDERS', userOrders: ordersCopy })
        gaEvent(gaEventCategories.orders, gaEvenActions.cancelled_trip, gaUserMapper(state), gaCancelledTripMapper(order))
        close()
      }).catch((err: any) => {
        setLoading(false)
        toast(err.response.data.message ? err.response.data.message : "ERROR")
      })
  }

  const orderStatus: Record<string, any> = {
    created: {
      label: 'Anmoder',
      style: 'statusStriped',
      iconStyle: 'iconGrey'
    },
    booked: {
      label: 'Bestilt',
      style: 'statusGreen',
      iconStyle: 'iconWhite'
    },
    dispatching: {
      label: 'Søger ledig taxi',
      style: 'statusBlue',
    },
    blocked: {
      label: 'Forsinket',
      style: 'statusYellow',
      iconStyle: 'iconWhite'
    },
    arriving: {
      label: 'Indenfor',
      style: 'statusBlue',
      iconStyle: 'iconWhite'
    },
    taximeter_started: {
      style: 'statusBlue',
      label: 'Afventer passager',
      iconStyle: 'iconWhite'
    },
    noshow: {
      label: 'Ingen passager',
      style: 'statusYellow',
      iconStyle: 'iconWhite'
    },
    trip_started: {
      label: 'Kører',
      style: 'statusGrey',
      iconStyle: 'iconWhite'
    },
    completed: {
      label: 'Afsluttet',
      style: 'statusWhite',
      iconStyle: 'iconGrey'
    },
    concluded: {
      label: 'Afsluttet',
      style: 'statusWhite',
      iconStyle: 'iconGrey'
    },
    cancelled: {
      label: 'Annulleret',
      style: 'statusRed',
      iconStyle: 'iconWhite'
    },
    deleted: {
      label: 'Annulleret',
      style: 'statusRed',
      iconStyle: 'iconWhite'
    }
  }

  const cars = {
    4: 'Alm. vogn (4 personer)',
    5: 'Stor vogn (5 personer)',
    6: 'Stor vogn (6 personer)',
    7: 'Stor vogn (7 personer)',
    8: 'Stor vogn (8 personer)'
  }

  return (
    <div ref={orderDetailsRef} className='tw-flex tw-flex-col tw-flex-full'>
      <header className={`${styles.header} ${orderStatus[order.state] ? styles[orderStatus[order.state].style] : ''}`}>
        <div className='tw-flex tw-flex-col'>
          <h4>{orderStatus[order.state] ? orderStatus[order.state].label : ''}
            <span className='opacity-60 tw-ml-2'>{
              order.tripFinishedAt ? format(new Date(order.tripFinishedAt), 'HH:mm') :
                order.estimatedTripDistance ? format(new Date(order.estimatedTripDistance), 'MM') :
                  order.estimatedMinutesToPickup ? differenceInMinutes(new Date(order.estimatedPickup), state.date) === 0 ? 'Mindre end 1 minut' : (differenceInMinutes(new Date(order.estimatedPickup), state.date) > 10 ? '10 min': `${differenceInMinutes(new Date(order.estimatedPickup), state.date)} min` ) : ''}
            </span></h4>
          <p className='tw-text-base  tw-font-normal'>{order.customerName}</p>
        </div>
        <span className={`${styles.closeIcon} ${orderStatus[order.state] ? styles[orderStatus[order.state].iconStyle] : ''}`} onClick={() => close()}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </header>
      <main className={styles.container}>
        <div className={styles.content}>
          <div className='tw-relative'>
            {(order.state !== 'concluded' && order.state !== 'completed') && !socketData && <div className={styles.spinnerContainer}>
              <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>}
            {geoJson ?
              geoJson.features && geoJson.features[0].geometry.coordinates.length > 0 ? <Map geoJson={geoJson} orderId={Number(currentOrderId)} taxiMarker={null} pickUpMarker={null} destinationMarker={null} /> :
                <p className='errorMsg'>Intet kortmateriale tilgængeligt</p>
              : taxiLocation || pickupLocation ?
                < Map taxiMarker={taxiLocation} pickUpMarker={pickupLocation} destinationMarker={destination} orderId={Number(currentOrderId)} />
                : <Spinner />}
          </div>
          <div className='tw-mt-3'>
            {order.tripFinishedAt && <p className={styles.deliveryInfo}>{`${format(new Date(order.tripFinishedAt), 'dd/MM/yy')} | ${format(new Date(order.requestedPickup), 'HH:mm')} - ${format(new Date(order.tripFinishedAt), 'HH:mm')}`}</p>}            <div className={styles.deliveryAddress}>
              <h5 className='tw-flex tw-mb-2 tw-items-center'>
                <svg className='tw-w-5 tw-h-5 fill-grey tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {order.pickupAddress.poi? order.pickupAddress.poi +', ':''}
                 {order.pickupAddress.streetName +' '+ order.pickupAddress.houseNo +', '} 
                 {order.pickupAddress.city? order.pickupAddress.city+', ':''}
                 {order.pickupAddress.zipCode?order.pickupAddress.zipCode:''}</h5>
              {order.destinationAddress && <h5 className='tw-flex tw-items-center'>
                <svg className='tw-w-5 tw-h-5 fill-grey tw-mr-3' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                </svg>
                {order.destinationAddress.poi? order.destinationAddress.poi +', ':''}
                 {order.destinationAddress.streetName +' '+ order.destinationAddress.houseNo +', '} 
                 {order.destinationAddress.city?order.destinationAddress.city+', ':''}
                 {order.destinationAddress.zipCode?order.destinationAddress.zipCode:''}
              </h5>}
            </div>
          </div>
          <div className={`${styles.orderInfo} ${!order.comment ? 'border-none' : ''}`}>
            {order.numberOfPeople ? <p>
              <span className={styles.labelLarge}>
                Taxi
              </span>
              {cars[order.numberOfPeople]}
            </p> : null}
            {order.actualTripDuration ? <p>
              <span className={styles.labelLarge}>
                Varighed
              </span>
              {Math.round((order.actualTripDuration / 60 + Number.EPSILON) * 100) / 100} min
            </p> : null}
            {order.finalPrice ? <p>
              <span className={styles.labelLarge}>
                Pris
              </span>
              {order.finalPrice} kr
            </p> : null}
            <p>
              <span className={styles.labelLarge}>
                Betalingsmiddel
              </span>
              {order.account ? 'Konto' : 'I taxi'}
            </p>
          </div>

          {order.comment && <p>
            <span className={`${styles.labelLarge} mt-5`}>Kommentar</span>
            {order.comment}
          </p>}
        </div>
        <aside className={styles.orderDetails}>
          <p>
            <span className={styles.label}>ID</span>
            {order.globalApiId ? order.globalApiId : '-'}
          </p>
          {referenceLabel && <p>
            <span className={styles.label}>{referenceLabel}</span>
            {order.reference ? order.reference : '-'}
          </p>}
          <p>
            <span className={styles.label}>Dato</span>
            {order.tripFinishedAt ? format(new Date(order.tripFinishedAt), 'dd/MM/yy') : '-'}
          </p>
          <p>
            <span className={styles.label}>Fastpris</span>
            {order.tripType ? order.tripType === 'FIXED' ? 'Ja' : 'Nej' : '-'}
          </p>
          <p>
            <span className={styles.label}>Tilvalg</span>
            {order.attributes.length ? order.attributes.map((attribute, i) => <span key={i} className='block'>{attribute.description}</span>) : '-'}
          </p>
          <p>
            <span className={styles.label}>Telefon</span>
            {order.phone ? order.phone : '-'}
          </p>
          <p>
            <span className={styles.label}>Køretøjsnr</span>
            {order.carNo ? order.carNo : '-'}
          </p>
          <p>
            <span className={styles.label}>Bestiller</span>
            {order.issuedByEmployee ? order.issuedByEmployee : '-'}
          </p>
        </aside>
      </main>
      <footer className={styles.footer}>
        <div className='tw-flex tw-items-center'>
          <button className={`${styles.navigationButton} ${styles.navigationLeft}`} disabled={!state.userOrders[currentIndex - 1]} onClick={() => {
            orderDetailsRef.current.scrollTo(0, 0)
            setDestination(null)
            setTaxiLocation(null)
            setPickupLocation(null)
            setCurrentOrderId(state.userOrders[currentIndex - 1].id)
          }}>
            <svg className='tw-h-6 tw-w-6' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          <button
            className={`${styles.navigationButton} ${styles.navigationRight}`}
            disabled={!state.userOrders[currentIndex + 1]}
            onClick={() => {
              setDestination(null)
              setTaxiLocation(null)
              setPickupLocation(null)
              orderDetailsRef.current.scrollTo(0, 0)
              setCurrentOrderId(state.userOrders[currentIndex + 1].id)
            }}>
            <svg className='tw-h-6 tw-w-6' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        {order.state !== 'created' && order.state !== 'booked' && order.state !== 'dispatching' && order.state !== 'arriving' && <button className={styles.printButton}>
          <ReactToPrint
            trigger={() => <span>Print</span>}
            content={() => orderDetailsRef.current}
          />
        </button>}
        {canCancel  && <Button disabled={loading} loading={loading} modifiers='tw-ml-auto' handleClick={cancelOrder} type='destructive' label='Annuller tur' />}
        {(order.state === 'created' || order.state === 'booked' || order.state === 'dispatching')  && order.numberOfPeople === 4 && <Button disabled={loading} loading={loading} modifiers='tw-ml-auto' handleClick={() => dispatch({ type: 'SHOW_ASIDE', asideType: 'EditOrder', show: true, orderId: order.id, referenceLabel: referenceLabel })} type='destructive' label='Opdater ordre' />}
      </footer>
    </div>
  )
}

export default OrdersDetails
